import React, { useEffect, useMemo, useRef, useState } from 'react'
import { isTablet, isMobileOnly } from 'react-device-detect'
import { Box, Button, Flex, Text, Image } from 'rebass'
import { useSupportedBrowser, usePortraitScreen } from 'uneeq-react-core'
import { useTranslation, Trans } from 'react-i18next'
import styles from './styles'

import portaitImg from '../assets/landing/LandingPageVideoPortrait.jpg'
import landingImg from '../assets/landing/LandingPageVideo.jpg'
import portaitVideo from '../assets/landing/LandingPageVideoPortrait.mp4'
import landingVideo from '../assets/landing/LandingPageVideo.mp4'
import MayaUnsupportedBrowser from '../../customPlugins/MayaUnsupportedBrowser'
import MayaCloseButton from '../../customPlugins/MayaCloseButton/MayaCloseButton'
import LanguageSelector from '../../customPlugins/LanguageSelector/LanguageSelector'
import { LanguageOption } from '../../customPlugins/LanguageSelector/LanguageOption'
import { Checkbox, Label } from '@rebass/forms'
import { useTheme } from 'emotion-theming'
import { useTvContext } from '../hooks/useTvContext'

interface SophieVideoProps {
  desktopOnly: boolean
  widgetMode: boolean
  renderLanguageSelector: () => JSX.Element
  isSmallScreen: boolean
}

const SophieVideo = ({
  desktopOnly,
  widgetMode,
  renderLanguageSelector,
  isSmallScreen
}: SophieVideoProps) => {
  const portraitScreen = usePortraitScreen()

  const shouldShowPortraitVideo = useMemo(() => {
    if (desktopOnly) {
      return portraitScreen
    }

    return isSmallScreen
  }, [desktopOnly, isSmallScreen, portraitScreen])

  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load()
    }
  }, [portraitScreen])

  const vid = document.getElementById('sophieVideo')

  //loop video
  useEffect(() => {
    const restartVideo = () => {
      if (vid) {
        // @ts-ignore
        vid.currentTime = 0.1 //setting to zero breaks iOS 3.2, the value won't update, values smaller than 0.1 was causing bug as well.
        // @ts-ignore
        vid.play()
      }
    }
    // @ts-ignore
    vid && vid.addEventListener('ended', restartVideo, false)
    return () => {
      // @ts-ignore
      vid && vid.removeEventListener('ended', restartVideo)
    }
  }, [vid])

  return (
    <Box sx={{ position: 'relative' }}>
      {widgetMode && !isSmallScreen && renderLanguageSelector()}
      <Box
        sx={widgetMode ? styles.sophieVideoWidget : styles.sophieVideo}
        as="video"
        ref={videoRef}
        id="sophieVideo"
        playsInline
        autoPlay
        muted
        loop
        poster={shouldShowPortraitVideo ? portaitImg : landingImg}
      >
        <source
          src={shouldShowPortraitVideo ? portaitVideo : landingVideo}
          type="video/mp4"
        />
      </Box>
    </Box>
  )
}

interface HomeProps {
  startSession: (speak: boolean) => void
  restart?: () => void
  headingMessage: string
  disableDigitalHuman: boolean
  logo: string
  widgetMode?: boolean
  embeddedMode: boolean
  languageSelected: string
  supportedLanguages: LanguageOption[]
  showVideoBackground?: boolean
  shouldAcceptTerms?: boolean
}
const Home: React.FC<HomeProps> = ({
  startSession,
  restart = () => {},
  headingMessage,
  disableDigitalHuman,
  logo,
  widgetMode = false,
  embeddedMode,
  languageSelected,
  supportedLanguages,
  showVideoBackground,
  shouldAcceptTerms
}) => {
  const { isBrowserSupported } = useSupportedBrowser()
  const { tvAppMode } = useTvContext()
  const isSmallScreen = isTablet || isMobileOnly
  const { t } = useTranslation()
  const [showUnsupportedAlert, setShowUnsupportedAlert] = useState(false)
  const [acceptedToS, setAcceptedToS] = useState(true)
  const theme = useTheme()

  const StartButton = ({ speak, sx, ...props }: any) => {
    const handleClick =
      speak && !isBrowserSupported
        ? () => setShowUnsupportedAlert(true)
        : () => startSession(speak)
    return (
      <Button
        {...props}
        onClick={handleClick}
        sx={{ ...styles.letsChatButton, ...sx }}
      >
        {t(speak ? 'Home.audioButton' : 'Home.chatButton')}
      </Button>
    )
  }

  const LanguageSelectorComp = () => (
    <LanguageSelector
      languageSelected={languageSelected}
      supportedLanguages={supportedLanguages}
    />
  )

  const ToSText = () => (
    <Text sx={styles.subtitle}>
      <Trans
        i18nKey="Home.privacy"
        components={{
          privacylink: (
            <Text
              as="a"
              href="https://mayamd.ai/privacy-policy/"
              target="_blank"
              rel="noreferrer noopener"
              sx={styles.privacyPolicyLink}
            />
          ),
          termslink: (
            <Text
              as="a"
              href="https://mayamd.ai/terms-and-conditions/"
              target="_blank"
              rel="noreferrer noopener"
              sx={styles.privacyPolicyLink}
            />
          )
        }}
      />
    </Text>
  )

  return (
    <Flex
      sx={{
        ...styles.mainContainer,
        ...(widgetMode
          ? {
              justifyContent: 'flex-end',
              position: 'static',
              flexDirection: 'column'
            }
          : {})
      }}
    >
      {showVideoBackground && (
        <SophieVideo
          widgetMode={widgetMode}
          desktopOnly={embeddedMode}
          renderLanguageSelector={() => <LanguageSelectorComp />}
          isSmallScreen={isSmallScreen}
        />
      )}
      {showUnsupportedAlert && (
        <MayaUnsupportedBrowser
          close={() => setShowUnsupportedAlert(false)}
          continueChatMode={() => {
            setShowUnsupportedAlert(false)
            startSession(false)
          }}
        />
      )}
      {logo && !widgetMode && (
        <Box variant="homeLogo">
          <Image src={logo} alt="Logo"></Image>
        </Box>
      )}
      {!widgetMode && !isSmallScreen && !tvAppMode && <LanguageSelectorComp />}
      <Flex
        variant="homeContainer"
        sx={{
          ...(widgetMode
            ? {
                m: 0
              }
            : {})
        }}
      >
        {isSmallScreen && !tvAppMode && (
          <LanguageSelector
            languageSelected={languageSelected}
            supportedLanguages={supportedLanguages}
            sx={{
              top: '0',
              transform: 'translateY(calc(-100% - 8px))',
              right: '0.5rem'
            }}
          />
        )}
        <Text variant="homeWelcome">
          <span>
            <Trans i18nKey="Home.welcome" components={{ bold: <strong /> }} />
          </span>
          <span>{t('Home.headingMessage') || headingMessage}</span>
        </Text>

        {shouldAcceptTerms && (
          <Flex sx={styles.acceptTerms}>
            <Label id="checkboxContainer" sx={{ width: 'fit-content' }}>
              <Checkbox
                checked={acceptedToS}
                onClick={() => setAcceptedToS(!acceptedToS)}
                sx={{
                  mr: 6,
                  cursor: 'pointer',
                  'input:checked ~ &': {
                    color: (theme as any).colors.checkboxChecked
                  }
                }}
              />
            </Label>
            <ToSText />
          </Flex>
        )}

        {headingMessage && (
          <Flex sx={styles.actions}>
            {!disableDigitalHuman && (
              <StartButton
                disabled={!acceptedToS}
                id="audio"
                variant="primary"
                speak={true}
              />
            )}
            <StartButton
              disabled={!acceptedToS}
              id="chat"
              variant="startChatbot"
              speak={false}
            />
          </Flex>
        )}
        {!shouldAcceptTerms && (
          <Flex variant="homeTerms" mt={4}>
            <ToSText />
            {widgetMode && (
              <MayaCloseButton
                customStyles={{ alignSelf: 'center' }}
                onClick={restart}
              />
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}
export default Home
